import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useCompanyPlan } from '../hooks/useCompanyPlan';
import Layout from '../components/Layout';
import {
  Search, ExternalLink, Download, Calendar,
  CheckCircle, XCircle, User, Mail, Phone,
  FileText, Award, Lock, RefreshCw, Eye,
  Video, MapPin, MessageSquare, Building,
  AlertCircle
} from 'lucide-react';

const STATUS_COLORS = {
  pending: '#F59E0B',
  reviewed: '#4F8EF7',
  shortlisted: '#10B981',
  interview_scheduled: '#7C3AED',
  hired: '#22D3EE',
  rejected: '#EF4444',
  withdrawn: '#94A3B8',
};

const STATUS_LABELS = {
  pending: 'Pending',
  reviewed: 'Under Review',
  shortlisted: 'Shortlisted',
  interview_scheduled: 'Interview',
  hired: 'Hired',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const STATUSES = [
  'all', 'pending', 'reviewed', 'shortlisted',
  'interview_scheduled', 'hired', 'rejected',
];

export default function Applications() {
  const { company, refreshCompany } = useAuth();
  const { can } = useCompanyPlan(company, refreshCompany);

  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterJob, setFilterJob] = useState('all');
  const [search, setSearch] = useState('');
  const [updating, setUpdating] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  // ── Core load — fetches by company_id directly ──────
  const load = useCallback(async () => {
    if (!company) return;
    setLoading(true);
    setError(null);

    try {
      // Step 1: Get the real company UUID
      // company object from useAuth may have id OR we need to look it up
      const { data: { user } } = await supabase.auth.getUser();

      // Try to find company by auth_user_id first (most reliable)
      const { data: companyRows } = await supabase
        .from('companies')
        .select('id, name')
        .eq('auth_user_id', user.id);

      // Fallback chain: auth_user_id lookup → company.id → company.company_id
      const realCompanyId =
        companyRows?.[0]?.id ||
        company?.id ||
        company?.company_id;

      if (!realCompanyId) {
        setError('Could not determine company ID. Please sign out and back in.');
        setLoading(false);
        return;
      }

      console.log('[Applications] Loading for company:', realCompanyId);

      // Step 2: Load ALL applications for this company — no joins first
      const { data: rawApps, error: appsErr } = await supabase
        .from('job_applications')
        .select('*')
        .eq('company_id', realCompanyId)
        .order('applied_at', { ascending: false });

      if (appsErr) {
        console.error('[Applications] Query error:', appsErr);
        setError(`Failed to load: ${appsErr.message}`);
        setLoading(false);
        return;
      }

      console.log('[Applications] Raw apps found:', rawApps?.length, rawApps);

      if (!rawApps || rawApps.length === 0) {
        setApps([]);
        setLoading(false);
        return;
      }

      // Step 3: Fetch related data separately (avoids join RLS issues)
      const studentIds = [...new Set(rawApps.map(a => a.student_id).filter(Boolean))];
      const jobIds = [...new Set(rawApps.map(a => a.job_id).filter(Boolean))];

      // Fetch student profiles
      let profileMap = {};
      if (studentIds.length > 0) {
        const { data: profiles } = await supabase
          .from('student_career_profiles')
          .select(`
            user_id, full_name, degree, university_name, major,
            city, country, cgpa, skills, resume_url,
            linkedin_url, github_url, portfolio_url,
            phone, gender, work_experience, projects,
            certifications, internships, achievements,
            preferred_job_types, year_of_study,
            graduation_date, expected_salary_min,
            expected_salary_max, available_from,
            open_to_relocation
          `)
          .in('user_id', studentIds);

        profileMap = Object.fromEntries(
          (profiles || []).map(p => [p.user_id, p])
        );
      }

      // Fetch job listings
      let jobMap = {};
      if (jobIds.length > 0) {
        const { data: jobList } = await supabase
          .from('job_listings')
          .select('id, title, category, job_type')
          .in('id', jobIds);

        jobMap = Object.fromEntries(
          (jobList || []).map(j => [j.id, j])
        );

        setJobs(jobList || []);
      }

      // Step 4: Enrich apps with profiles and jobs
      const enriched = rawApps.map(a => ({
        ...a,
        student_career_profiles: profileMap[a.student_id] || null,
        job_listings: jobMap[a.job_id] || null,
      }));

      setApps(enriched);
    } catch (err) {
      console.error('[Applications] Catch error:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [company]);

  useEffect(() => {
    if (company) load();
  }, [company, load]);

  // ── Update status + send notification ──────────────
  const updateStatus = async (appId, status, extra = {}, notifData = null) => {
    setUpdating(true);
    try {
      const update = {
        status,
        updated_at: new Date().toISOString(),
        decision_made_at: new Date().toISOString(),
        ...extra,
      };

      const { error: updateErr } = await supabase
        .from('job_applications')
        .update(update)
        .eq('id', appId);

      if (updateErr) {
        console.error('[updateStatus] error:', updateErr);
        showToast('❌ Failed to update: ' + updateErr.message);
        return;
      }

      // Update local state
      setApps(prev => prev.map(a =>
        a.id === appId ? { ...a, ...update } : a
      ));
      if (selected?.id === appId) {
        setSelected(prev => ({ ...prev, ...update }));
      }

      // Send in-app notification
      if (notifData) {
        const app = apps.find(a => a.id === appId);
        if (app?.student_id) {
          const { error: notifErr } = await supabase
            .from('notifications')
            .insert({
              user_id: app.student_id,
              type: notifData.type,
              title: notifData.title,
              body: notifData.body,
              data: {
                job_id: app.job_id,
                application_id: appId,
                ...(notifData.extra || {}),
              },
              is_read: false,
            });
          if (notifErr) console.error('Notification error:', notifErr);
        }
      }

      showToast(notifData?.toast || '✅ Updated successfully');
    } catch (err) {
      console.error('[updateStatus] catch:', err);
      showToast('❌ ' + err.message);
    } finally {
      setUpdating(false);
    }
  };

  const exportCSV = () => {
    if (!can.exportApplicants) return;
    const rows = [
      ['Name', 'Degree', 'University', 'CGPA', 'Status', 'Applied', 'Job'],
      ...apps.map(a => [
        a.student_career_profiles?.full_name || '',
        a.student_career_profiles?.degree || '',
        a.student_career_profiles?.university_name || '',
        a.student_career_profiles?.cgpa || '',
        a.status,
        new Date(a.applied_at).toLocaleDateString(),
        a.job_listings?.title || '',
      ]),
    ];
    const csv = rows.map(r => r.map(v =>
      `"${String(v).replace(/"/g, '""')}"`
    ).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `applications_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = apps.filter(a => {
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    if (filterJob !== 'all' && a.job_id !== filterJob) return false;
    if (search) {
      const name = a.student_career_profiles?.full_name?.toLowerCase() || '';
      if (!name.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const counts = STATUSES.reduce((acc, s) => {
    acc[s] = s === 'all'
      ? apps.length
      : apps.filter(a => a.status === s).length;
    return acc;
  }, {});

  return (
    <Layout>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.startsWith('❌') ? '#FFF1F2' : '#F0FDF4',
          border: `1px solid ${toast.startsWith('❌') ? '#FECDD3' : '#BBF7D0'}`,
          borderRadius: 12, padding: '12px 20px',
          fontSize: 13, fontWeight: 600,
          color: toast.startsWith('❌') ? '#E11D48' : '#16A34A',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          {toast}
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 130px)' }}>

        {/* ── Left Panel ── */}
        <div style={{
          width: 380, flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#1E293B' }}>
              Applications
            </h1>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#94A3B8' }}>
                {filtered.length} results
              </span>
              <button onClick={load} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 10px', borderRadius: 8,
                border: '1px solid #E2E8F0', background: '#fff',
                color: '#64748B', fontSize: 12, cursor: 'pointer',
              }}>
                <RefreshCw size={12} />
              </button>
              {can.exportApplicants ? (
                <button onClick={exportCSV} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '6px 12px', borderRadius: 8,
                  border: '1px solid #E2E8F0', background: '#fff',
                  color: '#475569', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                }}>
                  <Download size={12} />Export
                </button>
              ) : (
                <Link to="/billing" style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '6px 10px', borderRadius: 8,
                  border: '1px solid rgba(124,58,237,0.3)',
                  background: 'rgba(124,58,237,0.06)',
                  color: '#7C3AED', fontSize: 11, fontWeight: 700,
                  textDecoration: 'none',
                }}>
                  <Lock size={10} />Export
                </Link>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: '#FFF1F2', border: '1px solid #FECDD3',
              borderRadius: 10, padding: '10px 14px',
              display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              <AlertCircle size={14} color="#EF4444" style={{ marginTop: 1, flexShrink: 0 }} />
              <div style={{ fontSize: 12, color: '#9F1239' }}>{error}</div>
            </div>
          )}

          {/* Plan gate */}
          {!can.accessResumes && (
            <div style={{
              background: '#EFF6FF', border: '1px solid #BFDBFE',
              borderRadius: 10, padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <Lock size={13} color="#4F8EF7" />
              <div style={{ flex: 1, fontSize: 12, color: '#1D4ED8', fontWeight: 600 }}>
                Resume access requires Basic plan
              </div>
              <Link to="/billing" style={{
                padding: '4px 10px', borderRadius: 6,
                background: '#4F8EF7', color: '#fff',
                fontSize: 10, fontWeight: 700, textDecoration: 'none',
              }}>Upgrade</Link>
            </div>
          )}

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{
              position: 'absolute', left: 10,
              top: '50%', transform: 'translateY(-50%)', color: '#94A3B8',
            }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name..."
              style={{
                width: '100%', padding: '9px 12px 9px 32px',
                border: '1.5px solid #E2E8F0', borderRadius: 10,
                fontSize: 13, background: '#fff', outline: 'none',
              }} />
          </div>

          {/* Status filters */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {STATUSES.map(s => {
              const color = s === 'all' ? '#4F8EF7' : STATUS_COLORS[s] || '#4F8EF7';
              const label = s === 'all' ? 'All' : STATUS_LABELS[s];
              const active = filterStatus === s;
              return (
                <button key={s} onClick={() => setFilterStatus(s)} style={{
                  padding: '4px 10px', borderRadius: 6,
                  border: active ? `1.5px solid ${color}` : '1.5px solid #E2E8F0',
                  background: active ? `${color}15` : '#fff',
                  color: active ? color : '#64748B',
                  fontSize: 11, fontWeight: 600, cursor: 'pointer',
                }}>
                  {label} ({counts[s] ?? 0})
                </button>
              );
            })}
          </div>

          {/* Job filter */}
          <select value={filterJob} onChange={e => setFilterJob(e.target.value)}
            style={{
              padding: '8px 12px', border: '1.5px solid #E2E8F0',
              borderRadius: 10, fontSize: 13, background: '#fff',
              color: '#475569', cursor: 'pointer', outline: 'none',
            }}>
            <option value="all">All Jobs</option>
            {jobs.map(j => (
              <option key={j.id} value={j.id}>{j.title}</option>
            ))}
          </select>

          {/* List */}
          <div style={{
            flex: 1, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>
                Loading applications...
              </div>
            ) : filtered.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: 40,
                color: '#94A3B8', fontSize: 13, lineHeight: 1.6,
              }}>
                {apps.length === 0
                  ? <>
                      <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
                      <div style={{ fontWeight: 700, color: '#64748B', marginBottom: 4 }}>
                        No applications yet
                      </div>
                      <div>Applications from students will appear here.</div>
                      <div style={{ fontSize: 11, marginTop: 8, color: '#CBD5E1' }}>
                        If you applied a test application, click Refresh.
                      </div>
                    </>
                  : 'No applications match this filter.'}
              </div>
            ) : filtered.map(a => {
              const profile = a.student_career_profiles;
              const isSelected = selected?.id === a.id;
              const color = STATUS_COLORS[a.status] || '#94A3B8';
              return (
                <div key={a.id} onClick={() => setSelected(a)}
                  style={{
                    background: isSelected
                      ? 'linear-gradient(135deg,rgba(79,142,247,0.07),rgba(124,58,237,0.04))'
                      : '#fff',
                    borderRadius: 12, padding: 14, cursor: 'pointer',
                    border: isSelected
                      ? '1.5px solid rgba(79,142,247,0.35)'
                      : '1.5px solid #E2E8F0',
                    transition: 'all 0.15s',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 800, fontSize: 15,
                    }}>
                      {(profile?.full_name || 'U')[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 700, fontSize: 13, color: '#1E293B',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {profile?.full_name || `Student (${a.student_id?.slice(0,8)}...)`}
                      </div>
                      <div style={{
                        fontSize: 11, color: '#94A3B8',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {a.job_listings?.title || 'Unknown Job'} · {profile?.degree || 'N/A'}
                      </div>
                    </div>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '3px 6px',
                      borderRadius: 4, background: `${color}18`, color, flexShrink: 0,
                    }}>
                      {STATUS_LABELS[a.status] || a.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 6 }}>
                    Applied {new Date(a.applied_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })}
                    {a.shortlist_type && (
                      <span style={{
                        marginLeft: 8, color: '#7C3AED', fontWeight: 600,
                      }}>
                        · {a.shortlist_type === 'online' ? '🎥' : '🏢'} {a.shortlist_type}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right Panel ── */}
        <div style={{
          flex: 1, background: '#fff', borderRadius: 18,
          border: '1px solid #E2E8F0', overflowY: 'auto',
        }}>
          {!selected ? (
            <div style={{
              height: '100%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              color: '#94A3B8', gap: 12,
            }}>
              <User size={48} strokeWidth={1.5} />
              <div style={{ fontSize: 15, fontWeight: 600, color: '#64748B' }}>
                Select an application to view details
              </div>
            </div>
          ) : (
            <DetailPanel
              app={selected}
              can={can}
              company={company}
              onUpdate={updateStatus}
              updating={updating}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

// ── Detail Panel ──────────────────────────────────────
function DetailPanel({ app, can, company, onUpdate, updating }) {
  const profile = app.student_career_profiles;
  const status = app.status;
  const color = STATUS_COLORS[status] || '#94A3B8';
  const resumeUrl = profile?.resume_url || app.resume_url;

  const [modal, setModal] = useState(null); // 'shortlist' | 'hire' | 'reject'
  const [shortlistForm, setShortlistForm] = useState({
    type: 'online', message: '', venue: '', link: '', date: '',
  });
  const [rejectReason, setRejectReason] = useState('');
  const [hireMessage, setHireMessage] = useState('');

  const skills = (() => {
    const s = profile?.skills;
    if (Array.isArray(s)) return s;
    if (s && typeof s === 'object') return Object.values(s);
    return [];
  })();

  const handleShortlist = () => {
    onUpdate(
      app.id,
      'shortlisted',
      {
        shortlist_type: shortlistForm.type,
        shortlist_message: shortlistForm.message || null,
        interview_venue: shortlistForm.venue || null,
        interview_link: shortlistForm.link || null,
        interview_type: shortlistForm.type,
        interview_date: shortlistForm.date
          ? new Date(shortlistForm.date).toISOString() : null,
      },
      {
        type: 'application_shortlisted',
        title: '🏆 You\'ve been shortlisted!',
        body: `${company?.name || 'A company'} shortlisted you for ${app.job_listings?.title}. ` +
          (shortlistForm.type === 'online'
            ? 'Interview will be conducted online.'
            : 'Interview will be conducted in-person.') +
          (shortlistForm.message ? ` Message: ${shortlistForm.message}` : ''),
        extra: {
          interview_type: shortlistForm.type,
          interview_link: shortlistForm.link || null,
          interview_venue: shortlistForm.venue || null,
        },
        toast: `✅ ${profile?.full_name || 'Student'} shortlisted — notified in app`,
      }
    );
    setModal(null);
  };

  const handleHire = () => {
    onUpdate(
      app.id,
      'hired',
      {},
      {
        type: 'application_hired',
        title: '🎊 Congratulations! You\'ve been hired!',
        body: `${company?.name || 'A company'} selected you for ${app.job_listings?.title}!` +
          (hireMessage ? ` ${hireMessage}` : ' Congratulations!'),
        toast: `🎉 ${profile?.full_name || 'Student'} marked as hired!`,
      }
    );
    setModal(null);
  };

  const handleReject = () => {
    onUpdate(
      app.id,
      'rejected',
      { rejection_reason: rejectReason || null },
      {
        type: 'application_rejected',
        title: 'Application Update',
        body: `Your application for ${app.job_listings?.title} at ${company?.name || 'a company'} was not selected.` +
          (rejectReason ? ` Feedback: ${rejectReason}` : ''),
        toast: `Application rejected — student notified`,
      }
    );
    setModal(null);
    setRejectReason('');
  };

  return (
    <div style={{ padding: 28 }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 16,
        marginBottom: 20, paddingBottom: 18,
        borderBottom: '1px solid #F1F5F9',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 900, fontSize: 24,
        }}>
          {(profile?.full_name || 'U')[0].toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1E293B', margin: 0 }}>
              {profile?.full_name || 'Student Profile Not Found'}
            </h2>
            <span style={{
              padding: '4px 10px', borderRadius: 6, fontSize: 11,
              fontWeight: 700, background: `${color}18`, color,
            }}>
              {STATUS_LABELS[status] || status}
            </span>
            {app.shortlist_type && (
              <span style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                background: app.shortlist_type === 'online'
                  ? 'rgba(79,142,247,0.1)' : 'rgba(16,185,129,0.1)',
                color: app.shortlist_type === 'online' ? '#4F8EF7' : '#10B981',
              }}>
                {app.shortlist_type === 'online' ? '🎥 Online' : '🏢 Physical'} Interview
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
            {[
              profile?.degree,
              profile?.major && `(${profile.major})`,
              profile?.university_name,
              profile?.cgpa && `CGPA: ${profile.cgpa}`,
              profile?.year_of_study && `Year ${profile.year_of_study}`,
              (profile?.city || profile?.country) &&
                `${profile?.city || ''}${profile?.city && profile?.country ? ', ' : ''}${profile?.country || ''}`,
            ].filter(Boolean).map((t, i) => (
              <span key={i} style={{ fontSize: 12, color: '#94A3B8' }}>{t}</span>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
            Applied for:{' '}
            <strong style={{ color: '#4F8EF7' }}>
              {app.job_listings?.title || 'Unknown Job'}
            </strong>
            {' · '}
            {new Date(app.applied_at).toLocaleDateString('en-US', {
              month: 'long', day: 'numeric', year: 'numeric',
            })}
          </div>
        </div>
        {/* CV button */}
        {resumeUrl && (
          can.accessResumes ? (
            <a href={resumeUrl} target="_blank" rel="noreferrer" style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', background: '#F0FDF4',
              border: '1px solid #BBF7D0', borderRadius: 10,
              color: '#16A34A', fontSize: 12, fontWeight: 700,
              textDecoration: 'none', flexShrink: 0, whiteSpace: 'nowrap',
            }}>
              <FileText size={13} />View CV
            </a>
          ) : (
            <Link to="/billing" style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '8px 12px', background: 'rgba(79,142,247,0.06)',
              border: '1px solid rgba(79,142,247,0.2)', borderRadius: 10,
              color: '#4F8EF7', fontSize: 11, fontWeight: 700,
              textDecoration: 'none', flexShrink: 0,
            }}>
              <Lock size={11} />CV <span style={{
                fontSize: 8, background: '#4F8EF7', color: '#fff',
                borderRadius: 3, padding: '1px 4px', marginLeft: 2,
              }}>BASIC+</span>
            </Link>
          )
        )}
      </div>

      {/* ── Action Buttons ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {['pending', 'reviewed'].includes(status) && (
          <Btn label="👁 Mark Reviewed" color="#4F8EF7"
            onClick={() => onUpdate(app.id, 'reviewed')} loading={updating} />
        )}
        {['pending', 'reviewed'].includes(status) && (
          <Btn label="🏆 Shortlist" color="#10B981"
            onClick={() => setModal('shortlist')} />
        )}
        {['shortlisted', 'interview_scheduled'].includes(status) && (
          <Btn label="🎉 Hire" color="#22D3EE" primary
            onClick={() => setModal('hire')} />
        )}
        {!['rejected', 'hired', 'withdrawn'].includes(status) && (
          <Btn label="Reject" color="#EF4444" outline
            onClick={() => setModal('reject')} />
        )}
      </div>

      {/* ── Interview details banner ── */}
      {app.shortlist_type && (
        <div style={{
          background: app.shortlist_type === 'online'
            ? 'rgba(79,142,247,0.06)' : 'rgba(16,185,129,0.06)',
          border: `1px solid ${app.shortlist_type === 'online'
            ? 'rgba(79,142,247,0.25)' : 'rgba(16,185,129,0.25)'}`,
          borderRadius: 12, padding: 16, marginBottom: 20,
        }}>
          <div style={{
            fontWeight: 700, fontSize: 13, marginBottom: 10,
            color: app.shortlist_type === 'online' ? '#4F8EF7' : '#10B981',
          }}>
            {app.shortlist_type === 'online'
              ? '🎥 Online Interview Details'
              : '🏢 Physical Interview Details'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {app.interview_date && (
              <div style={{ fontSize: 12, color: '#475569' }}>
                📅 {new Date(app.interview_date).toLocaleString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </div>
            )}
            {app.interview_link && (
              <div style={{ fontSize: 12 }}>
                🔗{' '}
                <a href={app.interview_link} target="_blank" rel="noreferrer"
                  style={{ color: '#4F8EF7' }}>
                  {app.interview_link}
                </a>
              </div>
            )}
            {app.interview_venue && (
              <div style={{ fontSize: 12, color: '#475569' }}>
                📍 {app.interview_venue}
              </div>
            )}
            {app.shortlist_message && (
              <div style={{
                fontSize: 12, color: '#64748B', fontStyle: 'italic',
                marginTop: 4,
              }}>
                💬 "{app.shortlist_message}"
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Contact info ── */}
      {can.contactApplicants ? (
        (profile?.phone || profile?.linkedin_url ||
          profile?.github_url || profile?.portfolio_url) ? (
          <div style={{
            background: '#F0FDF4', border: '1px solid #BBF7D0',
            borderRadius: 12, padding: '12px 16px', marginBottom: 20,
            display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#065F46' }}>
              Contact:
            </span>
            {profile?.phone && (
              <a href={`tel:${profile.phone}`} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 12, color: '#10B981', fontWeight: 600,
              }}>
                <Phone size={12} />{profile.phone}
              </a>
            )}
            {profile?.linkedin_url && (
              <a href={profile.linkedin_url} target="_blank" rel="noreferrer" style={{
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 12, color: '#0077B5', fontWeight: 600,
              }}>
                <ExternalLink size={12} />LinkedIn
              </a>
            )}
            {profile?.github_url && (
              <a href={profile.github_url} target="_blank" rel="noreferrer" style={{
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 12, color: '#24292E', fontWeight: 600,
              }}>
                <ExternalLink size={12} />GitHub
              </a>
            )}
            {profile?.portfolio_url && (
              <a href={profile.portfolio_url} target="_blank" rel="noreferrer" style={{
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 12, color: '#7C3AED', fontWeight: 600,
              }}>
                <ExternalLink size={12} />Portfolio
              </a>
            )}
          </div>
        ) : null
      ) : (
        <div style={{
          background: 'rgba(79,142,247,0.06)',
          border: '1px solid rgba(79,142,247,0.2)',
          borderRadius: 12, padding: '10px 14px', marginBottom: 20,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Lock size={13} color="#4F8EF7" />
            <span style={{ fontSize: 12, color: '#4F8EF7', fontWeight: 600 }}>
              Contact details — Basic plan required
            </span>
          </div>
          <Link to="/billing" style={{
            padding: '5px 10px', borderRadius: 6,
            background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
            color: '#fff', fontSize: 10, fontWeight: 700,
            textDecoration: 'none',
          }}>Upgrade</Link>
        </div>
      )}

      {/* ── Info Grid ── */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: 14, marginBottom: 20,
      }}>
        <InfoBox title="Academic" icon={<Award size={13} />}>
          {[
            ['Degree', profile?.degree],
            ['Major', profile?.major],
            ['University', profile?.university_name],
            ['Year', profile?.year_of_study && `Year ${profile.year_of_study}`],
            ['CGPA', profile?.cgpa],
            ['Graduation', profile?.graduation_date
              ? new Date(profile.graduation_date).toLocaleDateString('en-US',
                  { month: 'short', year: 'numeric' }) : null],
          ].filter(([, v]) => v).map(([k, v]) => (
            <InfoRow key={k} k={k} v={v} />
          ))}
        </InfoBox>

        <InfoBox title="Personal" icon={<User size={13} />}>
          {[
            ['Gender', profile?.gender],
            ['Location', [profile?.city, profile?.country].filter(Boolean).join(', ')],
            ['Phone', can.contactApplicants ? profile?.phone : null],
            ['Available', profile?.available_from
              ? new Date(profile.available_from).toLocaleDateString() : null],
            ['Relocation', profile?.open_to_relocation != null
              ? (profile.open_to_relocation ? 'Yes' : 'No') : null],
            ['Exp. Salary', profile?.expected_salary_min
              ? `${profile?.salary_currency || 'PKR'} ${profile.expected_salary_min}${
                  profile.expected_salary_max ? `–${profile.expected_salary_max}` : '+'}`
              : null],
          ].filter(([, v]) => v).map(([k, v]) => (
            <InfoRow key={k} k={k} v={v} />
          ))}
        </InfoBox>

        <InfoBox title="Application" icon={<FileText size={13} />}>
          {[
            ['Job', app.job_listings?.title],
            ['Category', app.job_category],
            ['Applied', new Date(app.applied_at).toLocaleDateString()],
            ['Exp. Salary', app.expected_salary
              ? `PKR ${app.expected_salary?.toLocaleString()}` : null],
            ['Available', app.availability_date
              ? new Date(app.availability_date).toLocaleDateString() : null],
          ].filter(([, v]) => v).map(([k, v]) => (
            <InfoRow key={k} k={k} v={v} />
          ))}
          {Array.isArray(app.skills_highlighted) &&
            app.skills_highlighted.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 4 }}>
                HIGHLIGHTED SKILLS
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {app.skills_highlighted.map((s, i) => (
                  <span key={i} style={{
                    padding: '2px 7px', borderRadius: 4,
                    background: 'rgba(124,58,237,0.1)',
                    color: '#7C3AED', fontSize: 10,
                  }}>{s}</span>
                ))}
              </div>
            </div>
          )}
        </InfoBox>

        <InfoBox title="Skills" icon={<Award size={13} />}>
          {skills.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {skills.map((s, i) => (
                <span key={i} style={{
                  padding: '3px 8px', borderRadius: 5,
                  background: 'rgba(79,142,247,0.1)',
                  color: '#4F8EF7', fontSize: 11, fontWeight: 600,
                }}>
                  {typeof s === 'object' ? (s.name || JSON.stringify(s)) : s}
                </span>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: 12, color: '#94A3B8' }}>No skills listed</span>
          )}
        </InfoBox>
      </div>

      {/* ── Documents ── */}
      {can.accessResumes && (resumeUrl || app.cnic_url) && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B', marginBottom: 10 }}>
            📎 Documents
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {resumeUrl && (
              <DocBtn label="📄 CV / Resume" url={resumeUrl} color="#10B981" />
            )}
            {app.cnic_url && (
              <DocBtn label="🪪 CNIC / ID" url={app.cnic_url} color="#4F8EF7" />
            )}
            {Array.isArray(app.additional_docs) && app.additional_docs.map((d, i) => (
              <DocBtn key={i} label={d.label || `📎 Doc ${i + 1}`}
                url={d.url} color="#7C3AED" />
            ))}
          </div>
        </div>
      )}

      {/* ── Cover Letter ── */}
      {app.cover_letter && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B', marginBottom: 8 }}>
            Cover Letter
          </div>
          <div style={{
            background: '#F8FAFF', border: '1px solid #E2E8F0',
            borderRadius: 10, padding: 14, fontSize: 13,
            color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-wrap',
          }}>
            {app.cover_letter}
          </div>
        </div>
      )}

      {/* ── Work Experience ── */}
      {Array.isArray(profile?.work_experience) && profile.work_experience.length > 0 && (
        <Section title="Work Experience">
          {profile.work_experience.map((e, i) => (
            <div key={i} style={{
              display: 'flex', gap: 12, padding: '10px 14px',
              background: '#F8FAFF', border: '1px solid #E2E8F0',
              borderRadius: 10, marginBottom: 8,
            }}>
              <Building size={16} color="#4F8EF7" style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B' }}>
                  {e.role || e.title || 'Role'}
                </div>
                <div style={{ fontSize: 12, color: '#4F8EF7' }}>{e.company}</div>
                {e.duration && (
                  <div style={{ fontSize: 11, color: '#94A3B8' }}>{e.duration}</div>
                )}
                {e.description && (
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 4, lineHeight: 1.5 }}>
                    {e.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* ── Projects ── */}
      {Array.isArray(profile?.projects) && profile.projects.length > 0 && (
        <Section title="Projects">
          {profile.projects.map((p, i) => (
            <div key={i} style={{
              background: '#F8FAFF', border: '1px solid #E2E8F0',
              borderRadius: 10, padding: '10px 14px', marginBottom: 8,
            }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B' }}>{p.name}</div>
              {p.tech && <div style={{ fontSize: 11, color: '#4F8EF7', marginTop: 2 }}>{p.tech}</div>}
              {p.description && (
                <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4, lineHeight: 1.5 }}>
                  {p.description}
                </div>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* ── Certifications ── */}
      {Array.isArray(profile?.certifications) && profile.certifications.length > 0 && (
        <Section title="Certifications">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {profile.certifications.map((c, i) => (
              <span key={i} style={{
                padding: '5px 12px', borderRadius: 20,
                background: 'rgba(245,158,11,0.1)',
                border: '1px solid rgba(245,158,11,0.3)',
                color: '#F59E0B', fontSize: 11, fontWeight: 600,
              }}>
                🏅 {typeof c === 'object' ? (c.name || JSON.stringify(c)) : c}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* ── Rejection reason ── */}
      {status === 'rejected' && app.rejection_reason && (
        <div style={{
          background: '#FFF1F2', border: '1px solid #FECDD3',
          borderRadius: 12, padding: 14, marginBottom: 18,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#E11D48', marginBottom: 4 }}>
            REJECTION FEEDBACK
          </div>
          <div style={{ fontSize: 13, color: '#9F1239' }}>{app.rejection_reason}</div>
        </div>
      )}

      {/* ── Modals ── */}

      {/* Shortlist */}
      {modal === 'shortlist' && (
        <Modal onClose={() => setModal(null)}>
          <div style={{ fontWeight: 800, fontSize: 17, color: '#1E293B', marginBottom: 4 }}>
            🏆 Shortlist {profile?.full_name || 'Student'}
          </div>
          <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 18 }}>
            Student will be notified in-app instantly.
          </div>

          <Label>INTERVIEW TYPE *</Label>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, marginTop: 6 }}>
            {[
              { v: 'online', l: '🎥 Online', d: 'Video call / Meeting link' },
              { v: 'physical', l: '🏢 Physical', d: 'In-person at venue' },
            ].map(o => (
              <button key={o.v} onClick={() => setShortlistForm(f => ({ ...f, type: o.v }))}
                style={{
                  flex: 1, padding: '12px', borderRadius: 10, cursor: 'pointer',
                  textAlign: 'left',
                  border: shortlistForm.type === o.v
                    ? '2px solid #7C3AED' : '1.5px solid #E2E8F0',
                  background: shortlistForm.type === o.v
                    ? 'rgba(124,58,237,0.06)' : '#fff',
                  color: shortlistForm.type === o.v ? '#7C3AED' : '#64748B',
                }}>
                <div style={{ fontWeight: 700 }}>{o.l}</div>
                <div style={{ fontSize: 11, marginTop: 2, opacity: 0.7 }}>{o.d}</div>
              </button>
            ))}
          </div>

          <Label>DATE & TIME (optional)</Label>
          <input type="datetime-local" value={shortlistForm.date}
            onChange={e => setShortlistForm(f => ({ ...f, date: e.target.value }))}
            style={inputStyle} />

          {shortlistForm.type === 'online' && (
            <>
              <Label mt>MEETING LINK (optional)</Label>
              <input value={shortlistForm.link}
                onChange={e => setShortlistForm(f => ({ ...f, link: e.target.value }))}
                placeholder="https://meet.google.com/xxx or Zoom/Teams link"
                style={inputStyle} />
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
                Link will be shared with the student via notification.
              </div>
            </>
          )}

          {shortlistForm.type === 'physical' && (
            <>
              <Label mt>VENUE / ADDRESS</Label>
              <input value={shortlistForm.venue}
                onChange={e => setShortlistForm(f => ({ ...f, venue: e.target.value }))}
                placeholder="e.g. Head Office, 3rd Floor, Gulberg III, Lahore"
                style={inputStyle} />
            </>
          )}

          <Label mt>MESSAGE TO STUDENT (optional)</Label>
          <textarea value={shortlistForm.message}
            onChange={e => setShortlistForm(f => ({ ...f, message: e.target.value }))}
            placeholder={shortlistForm.type === 'online'
              ? 'e.g. We will send you the meeting link on WhatsApp before the interview.'
              : 'e.g. Please bring your original CNIC and academic documents.'}
            rows={3}
            style={{ ...inputStyle, resize: 'none' }} />

          <div style={{
            background: '#F0FDF4', border: '1px solid #BBF7D0',
            borderRadius: 10, padding: '10px 14px', marginTop: 14, marginBottom: 18,
            fontSize: 12, color: '#065F46',
          }}>
            ✅ Student will receive an in-app notification with all interview details immediately.
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setModal(null)} style={cancelBtnStyle}>Cancel</button>
            <button onClick={handleShortlist} disabled={updating} style={{
              ...confirmBtnStyle,
              background: 'linear-gradient(135deg,#10B981,#059669)',
              opacity: updating ? 0.6 : 1,
            }}>
              {updating ? 'Saving...' : '🏆 Confirm & Notify Student'}
            </button>
          </div>
        </Modal>
      )}

      {/* Hire */}
      {modal === 'hire' && (
        <Modal onClose={() => setModal(null)}>
          <div style={{ fontWeight: 800, fontSize: 17, color: '#1E293B', marginBottom: 4 }}>
            🎉 Hire {profile?.full_name || 'Student'}
          </div>
          <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 18 }}>
            Student will receive a congratulations notification in the app.
          </div>
          <Label>MESSAGE (optional)</Label>
          <textarea value={hireMessage}
            onChange={e => setHireMessage(e.target.value)}
            placeholder="e.g. Congratulations! Please report to HR on Monday at 9 AM with your documents."
            rows={4} style={{ ...inputStyle, resize: 'none', marginTop: 6 }} />
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button onClick={() => setModal(null)} style={cancelBtnStyle}>Cancel</button>
            <button onClick={handleHire} disabled={updating} style={{
              ...confirmBtnStyle,
              background: 'linear-gradient(135deg,#22D3EE,#0EA5E9)',
              opacity: updating ? 0.6 : 1,
            }}>
              {updating ? 'Saving...' : '🎉 Confirm Hire & Notify'}
            </button>
          </div>
        </Modal>
      )}

      {/* Reject */}
      {modal === 'reject' && (
        <Modal onClose={() => setModal(null)}>
          <div style={{ fontWeight: 800, fontSize: 17, color: '#1E293B', marginBottom: 4 }}>
            Reject Application
          </div>
          <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 18 }}>
            Student will be notified. Feedback is optional but helpful.
          </div>
          <Label>FEEDBACK (optional)</Label>
          <textarea value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="e.g. We are looking for candidates with more experience in React Native."
            rows={3} style={{ ...inputStyle, resize: 'none', marginTop: 6 }} />
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button onClick={() => setModal(null)} style={cancelBtnStyle}>Cancel</button>
            <button onClick={handleReject} disabled={updating} style={{
              ...confirmBtnStyle,
              background: '#EF4444',
              opacity: updating ? 0.6 : 1,
            }}>
              {updating ? 'Saving...' : 'Reject & Notify Student'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '10px 12px', fontSize: 13,
  border: '1.5px solid #E2E8F0', borderRadius: 10,
  outline: 'none', color: '#1E293B', background: '#fff',
  marginTop: 6,
};
const cancelBtnStyle = {
  flex: 1, padding: '12px 0', borderRadius: 10, cursor: 'pointer',
  border: '1px solid #E2E8F0', background: '#fff',
  color: '#64748B', fontWeight: 600, fontSize: 14,
};
const confirmBtnStyle = {
  flex: 2, padding: '12px 0', borderRadius: 10, cursor: 'pointer',
  border: 'none', color: '#fff', fontWeight: 700, fontSize: 14,
};

// ── Small helpers ─────────────────────────────────────
function Label({ children, mt = false }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: '#64748B',
      letterSpacing: 0.5, marginTop: mt ? 14 : 0,
    }}>
      {children}
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: 28,
        maxWidth: 520, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {children}
      </div>
    </div>
  );
}

function Btn({ label, color, onClick, loading, outline, primary }) {
  return (
    <button onClick={onClick} disabled={loading} style={{
      display: 'flex', alignItems: 'center', gap: 5,
      padding: '9px 16px', borderRadius: 9, cursor: 'pointer',
      border: outline ? `1.5px solid ${color}` : 'none',
      background: primary
        ? `linear-gradient(135deg,${color},${color}cc)`
        : outline ? `${color}10` : `${color}15`,
      color: primary ? '#fff' : color,
      fontSize: 12, fontWeight: 700,
      opacity: loading ? 0.6 : 1,
    }}>
      {label}
    </button>
  );
}

function InfoBox({ title, icon, children }) {
  return (
    <div style={{
      background: '#F8FAFF', border: '1px solid #E2E8F0',
      borderRadius: 12, padding: 14,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontWeight: 700, fontSize: 12, color: '#475569', marginBottom: 10,
      }}>
        {icon}{title}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ k, v }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      fontSize: 12, padding: '4px 0',
      borderBottom: '1px solid #F1F5F9',
    }}>
      <span style={{ color: '#94A3B8' }}>{k}</span>
      <span style={{
        color: '#1E293B', fontWeight: 600, maxWidth: 170,
        textAlign: 'right', overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{v}</span>
    </div>
  );
}

function DocBtn({ label, url, color }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" style={{
      display: 'flex', alignItems: 'center', gap: 7,
      padding: '10px 16px', borderRadius: 10,
      border: `1px solid ${color}40`,
      background: `${color}10`, color,
      fontSize: 12, fontWeight: 700, textDecoration: 'none',
    }}>
      {label} <ExternalLink size={11} />
    </a>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B', marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </div>
  );
}